import { useMutation, useQueryClient, UseMutationOptions } from '@tanstack/react-query';
import { updateBehaviourActionState } from '@/services/DataService';

export interface UpdateBehaviourActionStateParams {
  workflowInstanceId: string;
  workflowExecutionId: string;
  chatHistoryId: string;
  behaviourActionStateId: string;
  state: Record<string, unknown>;
}

/**
 * Hook to update a behaviour action state with an arbitrary state object.
 * Invalidates the chatHistory query on success to refetch updated state.
 */
export const useUpdateBehaviourActionState = (
  options?: Omit<UseMutationOptions<unknown, Error, UpdateBehaviourActionStateParams>, 'mutationFn'>
) => {
  const queryClient = useQueryClient();

  return useMutation({
    ...options,
    mutationFn: ({
      workflowInstanceId,
      workflowExecutionId,
      chatHistoryId,
      behaviourActionStateId,
      state
    }: UpdateBehaviourActionStateParams) => {
      return updateBehaviourActionState(
        workflowInstanceId,
        workflowExecutionId,
        chatHistoryId,
        behaviourActionStateId,
        state
      );
    },
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({
        queryKey: ['chatHistory', variables.workflowInstanceId, variables.workflowExecutionId]
      });
      options?.onSuccess?.(data, variables, context);
    },
  });
};
